import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Check, 
  Copy, 
  AlertCircle, 
  X, 
} from 'lucide-react';
import { ContractWizardState, SchemaDefinition } from '../../../types/contract';
import { Integration } from '../../../types/integration';
import { fetchAllSchemaDefinitions } from '../../../services/schemaDefinitionApiService';
import { integrationService } from '../../../services/integration.service';

interface SummaryStepProps {
  state: ContractWizardState;
  updateState: (updates: Partial<ContractWizardState>) => void;
  saveAsDraft: () => Promise<any>;
  isFinalizing: boolean; // New prop for finalization state
}

const SummaryStep: React.FC<SummaryStepProps> = ({ state, updateState }) => {
  
  const [schemaDefinitions, setSchemaDefinitions] = useState<SchemaDefinition[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  
  // Load schema definitions and integrations
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        const [schemasData, integrationsResponse] = await Promise.all([
          fetchAllSchemaDefinitions(),
          integrationService.getIntegrations()
        ]);
        const integrationsData = integrationsResponse.data;
        
        setSchemaDefinitions(schemasData);
        setIntegrations(integrationsData);
      } catch (error) {
        console.error('Failed to load data:', error);
        setError('Failed to load schema definitions and integrations');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Get selected schemas
  const selectedSchemas = schemaDefinitions.filter(schema => 
    state.selectedSchemaIds.includes(schema.id)
  );
  
  // Get selected integrations
  const selectedIntegrations = integrations.filter(integration => 
    state.selectedIntegrationIds.includes(integration.id)
  );
  
  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    if (!state.shareToken) return;
    
    const shareLink = `${window.location.origin}/contracts/share/${state.shareToken}`;
    
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError('Failed to copy to clipboard');
      });
  };
  
  // Calculate mapping progress percentage
  const mappingProgressPercentage = state.mappingProgress.total > 0
    ? Math.round((state.mappingProgress.mapped / state.mappingProgress.total) * 100)
    : 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Summary and Review</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Review your contract details, add customer information, and finalize your contract.
        </p>
      </div>
      
      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 flex items-start"
        >
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-auto text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
          >
            <X className="h-5 w-5" />
          </button>
        </motion.div>
      )}
      
      {/* Contract Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Contract Summary</h3>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Basic Details */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Basic Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Contract Name</p>
                <p className="mt-1 text-base font-medium text-gray-900 dark:text-white">{state.name}</p>
              </div>
              
              {state.description && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</p>
                  <p className="mt-1 text-base text-gray-900 dark:text-white">{state.description}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Schema Definitions */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Schema Definitions</h4>
            {isLoading ? (
              <p className="text-gray-500 dark:text-gray-400">Loading...</p>
            ) : selectedSchemas.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedSchemas.map(schema => (
                  <span
                    key={schema.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  >
                    {schema.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No schema definitions selected</p>
            )}
          </div>
          
          {/* Integrations */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Integrations</h4>
            {isLoading ? (
              <p className="text-gray-500 dark:text-gray-400">Loading...</p>
            ) : selectedIntegrations.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedIntegrations.map(integration => (
                  <span
                    key={integration.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  >
                  <img 
                    src={integration.logo_url} 
                    alt={integration.name} 
                    className="h-4 w-4 mr-1.5"
                  />
                    {integration.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No integrations selected</p>
            )}
          </div>
          
          {/* Events */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Events</h4>
            {state.events.length > 0 ? (
              <div className="space-y-2">
                {state.events.map(event => (
                  <div key={event.id} className="flex items-center">
                    <Check className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-900 dark:text-white">{event.name}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400 text-sm">
                      ({event.properties.length} properties)
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No events added</p>
            )}
          </div>
          
          {/* Mapping Progress */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Mapping Progress</h4>
            <div className="mb-2">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {state.mappingProgress.mapped} of {state.mappingProgress.total} properties mapped
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {mappingProgressPercentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${mappingProgressPercentage}%` }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Required: {state.mappingProgress.requiredMapped} / {state.mappingProgress.required}
                </p>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-md p-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Optional: {state.mappingProgress.mapped - state.mappingProgress.requiredMapped} / {state.mappingProgress.total - state.mappingProgress.required}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Customer Details */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Customer Details</h3>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Customer Name
              </label>
              <input
                type="text"
                id="customer-name"
                value={state.customerName || ''}
                onChange={(e) => updateState({ customerName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                placeholder="Enter customer name"
              />
            </div>
            
            <div>
              <label htmlFor="customer-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Customer Email
              </label>
              <input
                type="email"
                id="customer-email"
                value={state.customerEmail || ''}
                onChange={(e) => updateState({ customerEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                placeholder="Enter customer email"
              />
            </div>
          </div>
        </div>
      </div>
      
      {state.shareToken && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Share Link</h3>
          </div>
          
          <div className="p-6">
            <div className="flex items-center">
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-md px-4 py-2 text-gray-900 dark:text-white font-mono text-sm truncate">
                  {`${window.location.origin}/contracts/share/${state.shareToken}`}
                </div>
                <button
                  type="button"
                  onClick={handleCopyToClipboard}
                  className="ml-2 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 relative"
                >
                  <Copy className="h-5 w-5" />
                  {showCopiedMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded"
                    >
                      Copied!
                    </motion.div>
                  )}
                </button>
              </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SummaryStep;
